import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class ApparelMixMatch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
    this.initAccessories();
    this.rows = [...this.querySelectorAll('.gmm-row')];
    const initial = this.data.variants.find(v => String(v.id) === this.dataset.initialVariant && v.available) || this.data.variants.find(v => v.available) || this.data.variants[0];
    this.rows.forEach((row,index) => {
      const variant = index === 0 ? initial : this.data.variants.find(v => v.available && v.id !== initial?.id) || initial;
      row.querySelectorAll('[data-option]').forEach(select => select.value = variant?.options[Number(select.dataset.option)-1] || select.options[0]?.value);
    });
    this.button = this.querySelector('.gmm-add');
    this.status = this.querySelector('.gmm-status');
    this.addEventListener('change', () => {this.justAdded = false; this.render();});
    this.button.addEventListener('click', () => this.add());
    this.onCart = async event => {
      try { await event.promise; await this.refreshCart(); } catch { /* Preserve last estimate. */ }
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    this.render();
    this.refreshCart().catch(() => {});
  }
  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    this.initialized = false;
  }
  initAccessories() {
    const pool = [...new Map((this.data.accessories || []).filter(Boolean).map(p => [p.id,p])).values()]
      .map(p => ({...p,variants:p.variants.filter(v => v.available)})).filter(p => p.variants.length);
    for (let i=pool.length-1;i>0;i--) {const j=Math.floor(Math.random()*(i+1)); [pool[i],pool[j]]=[pool[j],pool[i]];}
    this.extras = pool.slice(0,Math.min(pool.length,1+Math.floor(Math.random()*3)));
    const host = this.querySelector('[data-accessory-list]');
    host.replaceChildren();
    this.querySelector('[data-accessories]').hidden = !this.extras.length;
    for (const p of this.extras) {
      const card=document.createElement('div'); card.className='gmm-accessory'; card.dataset.accessoryCard=String(p.id);
      const checkbox=document.createElement('input'); checkbox.type='checkbox'; checkbox.dataset.accessory=''; checkbox.setAttribute('aria-label','Add '+p.title);
      const img=document.createElement('img'); img.src=p.image; img.alt=p.title; img.width=64; img.height=64; img.loading='lazy';
      const info=document.createElement('div'); info.className='gmm-extra-info';
      const title=document.createElement('a'); title.href=p.url; title.textContent=p.title;
      const price=document.createElement('small'); price.dataset.extraPrice='';
      const select=document.createElement('select'); select.setAttribute('aria-label',p.title+' variant');
      for (const v of p.variants) {const option=document.createElement('option'); option.value=String(v.id); option.textContent=v.title; select.append(option);}
      select.hidden=p.variants.length===1;
      info.append(title,price,select); card.append(checkbox,img,info); host.append(card);
    }
  }
  root() { return window.Shopify?.routes?.root || '/'; }
  async refreshCart() {
    const response = await fetch(`${this.root()}cart.js`, {credentials:'same-origin',cache:'no-store'});
    if (!response.ok) throw Error('Unable to read cart');
    this.cart = await response.json(); this.render(); return this.cart;
  }
  selection() {
    const count = Number(this.querySelector('.gmm-tiers input:checked').value);
    const variants = this.rows.slice(0, count).map(row => this.data.variants.find(v =>
      [...row.querySelectorAll('[data-option]')].every(select => v.options[Number(select.dataset.option)-1] === select.value)));
    const accessories = [...this.querySelectorAll('[data-accessory-card]')].filter(card => card.querySelector('[data-accessory]').checked).map(card => {
      const product = this.extras.find(p => String(p.id) === card.dataset.accessoryCard);
      return product.variants.find(v => String(v.id) === card.querySelector('select').value);
    });
    return { count, variants, accessories, chosen: [...variants, ...accessories] };
  }
  money(value) { return new Intl.NumberFormat('en-US', {style:'currency',currency:this.cart?.currency || 'USD'}).format(value / 100); }
  render() {
    const s = this.selection();
    this.querySelectorAll('[data-accessory-card]').forEach(card => {
      const p=this.extras.find(p => String(p.id)===card.dataset.accessoryCard);
      const v=p.variants.find(v => String(v.id)===card.querySelector('select').value);
      card.querySelector('[data-extra-price]').textContent=this.money(v.price);
    });
    this.rows.forEach((row,i) => {
      row.hidden = i >= s.count;
      row.querySelector('.gmm-availability').textContent = i < s.count && !s.variants[i]?.available ? 'This combination is sold out. Please choose another.' : '';
    });
    this.button.disabled = !!this.busy || s.chosen.some(v => !v?.available);
    this.button.textContent = this.busy ? 'Adding…' : `Add ${s.count} ${s.count === 1 ? 'item' : 'items'}${s.accessories.length ? ` + ${s.accessories.length} ${s.accessories.length === 1 ? 'extra' : 'extras'}` : ''} to cart`;
    const subtotal = s.chosen.reduce((sum,v) => sum + (v?.price || 0),0);
    const discount = s.chosen.length >= 3 ? 1000 : s.chosen.length >= 2 ? 500 : 0;
    const usd = !this.cart || this.cart.currency === 'USD';
    this.querySelector('.gmm-summary').textContent = usd ? `Selected items: ${this.money(subtotal - discount)}${discount ? ` · Save ${this.money(discount)} (estimated)` : ''}` : `Selected items: ${this.money(subtotal)} before automatic discounts`;
    let shipping;
    if (!usd) shipping = 'Free shipping eligibility is calculated at checkout in your currency.';
    else {
      // Match the visible selected-items summary; do not silently include existing cart lines.
      const projected = this.justAdded ? this.cart.total_price : Math.max(0, subtotal - discount);
      const gap = Math.max(0, 6990 - projected);
      const basis = this.justAdded ? 'Current cart after discounts' : 'Selected items after estimated discounts';
      shipping = gap > 0
        ? `${basis}: ${this.money(projected)}. Add ${this.money(gap)} more to reach FREE standard shipping on ${this.money(6990)}+ orders for eligible destinations.`
        : `${basis}: ${this.money(projected)}. The ${this.money(6990)} FREE standard shipping threshold is reached for eligible destinations; final eligibility is confirmed at checkout.`;
    }
    this.querySelector('.gmm-shipping').textContent = shipping;
  }
  async add() {
    if (this.busy) return;
    const s = this.selection();
    if (s.chosen.some(v => !v?.available)) return;
    const quantities = new Map();
    s.chosen.forEach(v => quantities.set(v.id,(quantities.get(v.id) || 0) + 1));
    const items = [...quantities].map(([id,quantity]) => ({id,quantity}));
    this.busy = true; this.status.removeAttribute('data-error'); this.status.textContent = ''; this.render();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(x => ({merchandiseId:String(x.id),quantity:x.quantity})),promise:deferred.promise}));
    let added = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(x => x.dataset.sectionId).filter(Boolean))];
      const response = await fetch(`${this.root()}cart/add.js`,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({items,sections:sections.join(','),sections_url:location.pathname})});
      const result = await response.json();
      if (!response.ok || result.status) throw Error(result.description || result.message || 'Unable to add these items. Please check availability.');
      added = true;
      const cart = await this.refreshCart();
      this.justAdded = true;
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'product-form-component',sourceId:this.id,itemCount:s.chosen.length,productId:this.dataset.productId,sections:result.sections,didError:false}});
      this.status.textContent = `Added ${s.count} ${s.count === 1 ? 'item' : 'items'}${s.accessories.length ? ` and ${s.accessories.length} ${s.accessories.length === 1 ? 'extra' : 'extras'}` : ''}. Cart total after discounts: ${this.money(cart.total_price)}.`;
    } catch(error) {
      try {
        const cart = await this.refreshCart();
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{didError:true,items:cart.items,source:'product-form-component',sourceId:this.id}});
      } catch { deferred.reject(error); }
      this.status.setAttribute('data-error','');
      this.status.textContent = added ? 'Items were added, but the cart preview could not refresh. Open your cart to check them before adding again.' : `${error.message} Check your cart before retrying if the connection was interrupted.`;
    } finally { this.busy = false; this.render(); }
  }
}
if (!customElements.get('apparel-mix-match')) customElements.define('apparel-mix-match', ApparelMixMatch);
